/**
 * Tenant Context
 * Manages current tenant state and tenant-related operations
 */

import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import tenantService from '../services/tenantService';
import { isPlatformRole } from '../constants/roles';

const TenantContext = createContext(null);

export function TenantProvider({ children }) {
  const { user, isAuthenticated } = useAuth();
  const [currentTenant, setCurrentTenant] = useState(null);
  const [tenants, setTenants] = useState([]); // For platform admins
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Determine if user is a platform admin (can switch tenants)
  const isPlatformAdmin = user && isPlatformRole(user.role);

  // Load tenant data on auth change
  useEffect(() => {
    const loadTenantData = async () => {
      if (!isAuthenticated || !user) {
        setCurrentTenant(null);
        setTenants([]);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      try {
        if (isPlatformAdmin) {
          // Platform admin - load all tenants
          const allTenants = await tenantService.getAllTenants();
          setTenants(allTenants);
          // Set first tenant as default or from localStorage
          const savedTenantId = localStorage.getItem('srop_selected_tenant');
          const savedTenant = allTenants.find(t => t.id === savedTenantId);
          setCurrentTenant(savedTenant || allTenants[0] || null);
        } else if (user.tenant_id) {
          // Regular user - load their tenant
          const tenant = await tenantService.getTenant(user.tenant_id);
          setCurrentTenant(tenant);
          setTenants([tenant]);
        }
      } catch (err) {
        console.error('Failed to load tenant data:', err);
        // Fallback for demo mode
        if (user.tenant_id === 'demo_client' || user.tenant_id === 'ourbus') {
          const demoTenant = {
            id: user.tenant_id,
            name: user.tenant_id === 'ourbus' ? 'OurBus' : 'Demo Company',
            slug: user.tenant_id,
            status: 'active',
            plan: 'pro',
            branding: {
              primary_color: '#2563eb',
              logo_url: null,
            },
          };
          setCurrentTenant(demoTenant);
          setTenants([demoTenant]);
        } else {
          setError(err.message);
        }
      } finally {
        setIsLoading(false);
      }
    };

    loadTenantData();
  }, [isAuthenticated, user, isPlatformAdmin]);

  // Switch tenant (platform admins only)
  const switchTenant = useCallback(async (tenantId) => {
    if (!isPlatformAdmin) {
      throw new Error('Only platform admins can switch tenants');
    }

    const tenant = tenants.find(t => t.id === tenantId);
    if (!tenant) {
      throw new Error('Tenant not found');
    }

    setCurrentTenant(tenant);
    localStorage.setItem('srop_selected_tenant', tenantId);
  }, [isPlatformAdmin, tenants]);

  // Refresh current tenant data
  const refreshTenant = useCallback(async () => {
    if (!currentTenant) return;
    
    try {
      const updated = await tenantService.getTenant(currentTenant.id);
      setCurrentTenant(updated);
    } catch (err) {
      console.error('Failed to refresh tenant:', err);
    }
  }, [currentTenant]);

  // Get tenant ID for API calls
  const getTenantId = useCallback(() => {
    return currentTenant?.id || user?.tenant_id || null;
  }, [currentTenant, user]);

  const value = {
    currentTenant,
    tenants,
    isLoading,
    error,
    isPlatformAdmin,
    switchTenant,
    refreshTenant,
    getTenantId,
    canSwitchTenants: isPlatformAdmin && tenants.length > 1,
  };

  return (
    <TenantContext.Provider value={value}>
      {children}
    </TenantContext.Provider>
  );
}

export function useTenant() {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
}

export default TenantContext;

